<template>
  <div class="filters__sidebar" :class="{ 'filters--sidebar--open' : sidebarVisible}">

    <div class="filters__mobile-wrapper">

      <div class="filters__header">
        <div class="filters__counter">
          Filters ({{ counter }})
        </div>
        <div @click=toggleSidebar() class="filters__close">
          <svg-vue icon="close-icon"></svg-vue>
        </div>
      </div>

      <SelectOption v-if="collection !== 'pages' && origin === 'publications'" v-model="filters.publication_types" :options="publication_types" label="Publication Type" defaultValue="All Types" />

      <SelectOption v-if="collection === 'programs'" v-model="filters.related_projects" :options="projects" label="Projects" defaultValue="All Projects" />

      <SelectOption v-if="collection === 'pages' && origin === 'projects'" v-model="filters.project_type" :options="types" label="Project Type" defaultValue="All Types" />

      <SelectOption v-if="collection === 'pages'" v-model="filters.related_areas_of_investigation" :options="areas" label="Carbon Sources" defaultValue="All Sources" />

      <SelectOption v-if="collection === 'pages'" v-model="filters.related_programs" :options="programs" label="Programs" defaultValue="All Programs" />

      <SelectOption v-if="collection === 'pages' && origin === 'projects'" v-model="filters.project_phase" :options="phases" label="Phase" defaultValue="All Phases" />

      <div class="filters__checkboxes">
        <div class="text--caption">Methods</div>
        <div class="checkbox__wrapper" v-for="(method) in methods">
          <input type="checkbox" v-model= "filters.method" name="method" :value="method.title" :id="method.title">
          <label :for="method.title">{{ method.title }}</label>
        </div>
      </div>

    </div>

    <div class="filters__footer">
      <div @click="fetchResults(filterString); toggleSidebar()" class="footer__control">
        Apply <template v-if="!counter">Filters</template>
      </div>
      <div class="footer__control" @click="clearFilters()" v-if="counter">
        Clear
      </div>
    </div>

  </div>  

  <div class="filters__results">

    <div @click=toggleSidebar() class="filters__toggle">
      Filter By...
      <svg-vue icon="filter"></svg-vue>
    </div>

    <template v-if="filterResults.length > 0">
      <ResultTile :tiles="filterResults" :images="filterImages" :collection="collection"/>
    </template>

    <template v-else >
      <div class="filters__message">
        There are no results for this query. Please update your filters to view results.
      </div>

    </template>

  </div>

</template>

<script>

import ResultTile from './filters/ResultTile.vue'
import SelectOption from './filters/SelectOption.vue'

export default {
  name: "FilterResults",
  props: ['id', 'collection', 'origin'],
  components: {ResultTile, SelectOption},
  data() {
    return {
      counter: 0,
      sidebarVisible: false,
      filters: {
        related_areas_of_investigation: '',
        related_programs: '',
        related_projects: '',
        publication_types: '',
        project_type: '',
        project_phase: '',
        method: [],
      },
      filterString: '',
      filterResults: [],
      filterImages: [],
      areas: [],
      programs: [],
      projects: [],
      publication_types: [],
      types: {
        application: 'Application',
        market_creation: 'Market Creation',
        novel_development: 'Novel Development',
        report: 'Report',
        study: 'Study'
      },
      phases: {
        investigation: 'Investigation',
        experimentation: 'Experimentation',
        implementation: 'Implementation',
        completed: 'Completed',
        inactive: 'Inactive'
      },
      methods: [],
    }
  },
  mounted() {
    this.fetchResults();
    this.setupFilters();
  },
  watch: {
    'filters.related_areas_of_investigation'() {
      this.updateFilters();
    },
    'filters.related_programs'() {
      this.updateFilters();
    },
    'filters.related_projects'() {
      this.updateFilters();
    },
    'filters.publication_types'() {
      this.updateFilters();
    },
    'filters.project_type'() {
      this.updateFilters();
    },
    'filters.project_phase'() {
      this.updateFilters();
    },
    'filters.method'() {
      this.updateFilters();
    },
  },
  methods: {
    async fetchResults(filters) {

      const baseAPI = `/api/collections/${this.origin}/entries?`
      let baseRoute = ''
      
      if (this.origin === 'projects'){
        baseRoute = baseAPI;
      } else {
        if (this.collection === 'programs') {
          baseRoute = baseAPI.concat(`filter[related_programs:contains]=${this.id}`);
        } else if (this.collection === 'projects') {
          baseRoute = baseAPI.concat(`filter[related_projects:contains]=${this.id}`);
        } else {
          baseRoute = baseAPI.concat('sort=-date&')
        }
      }

      const fetchData = await fetch(`${baseRoute}${filters ? filters : ''}`);
      const { data } = await fetchData.json();
      this.filterResults = data;
     
      this.filterImages = [];
      this.filterResults.forEach(item => {
        if (this.origin === 'projects' || this.origin === 'posts'){
          this.filterImages.push(`${item.hero_background_media.url}`)
        } else {
          this.filterImages.push(`${item.publication_hero_background_media.url}`)
        }
      })

    },

    async setupFilters() {
      const fetchAreasData = await fetch(`/api/collections/areas_of_investigation/entries/`);
      const areasData = await fetchAreasData.json();
      const massageAreasData = [];
      areasData.data.forEach(area => {
        massageAreasData.push({id: area.id, title: area.title});
      });
      this.areas = massageAreasData;

      const fetchProgramsData = await fetch(`/api/collections/programs/entries/`);
      const programsData = await fetchProgramsData.json();
      const massageProgramsData = [];
      programsData.data.forEach(program => {
        massageProgramsData.push({id: program.id, title: program.title});
      });
      this.programs = massageProgramsData;

      const fetchProjectsData = await fetch(`/api/collections/projects/entries`);
      const projectsData = await fetchProjectsData.json();
      const massageProjectsData = [];
      projectsData.data.forEach(project => {
        massageProjectsData.push({id: project.id, title: project.title});
      });
      this.projects = massageProjectsData;

      const fetchPublicationsData = await fetch(`/api/taxonomies/publication_types/terms`);
      const publicationsData = await fetchPublicationsData.json();
      const massagePublicationsData = [];
      publicationsData.data.forEach(publication => {
        massagePublicationsData.push({id: publication.slug, title: publication.title});
      });
      this.publication_types = massagePublicationsData;

      const fetchMethodsData = await fetch(`/api/taxonomies/method/terms`);
      const methodsData = await fetchMethodsData.json();
      const massageMethodsData = [];
      methodsData.data.forEach(method => {
        massageMethodsData.push({id: method.slug, title: method.title});
      });
      this.methods = massageMethodsData;
    },

    updateFilters() {
      const filterItems = []
      
      for (const [key, value] of Object.entries(this.filters)) {
        if (typeof value === 'string'){
          if (value !== '') {
            filterItems.push(`filter[${key}:contains]=${value}`)
          }
        } else {
          value.forEach(item => {
            filterItems.push(`filter[method:contains]=${item}`)
          })
        }
      }

      this.counter = filterItems.length;
      this.filterString = `${filterItems.join('&')}`

      if (window.innerWidth > 600) {
        this.fetchResults(this.filterString);
      }

    },

    clearFilters() {
      this.filters.related_areas_of_investigation = '';
      this.filters.related_programs = '';
      this.filters.related_projects = '';
      this.filters.publication_types = '';
      this.filters.project_type = '';
      this.filters.project_phase ='';
      this.filters.method = [];
    },

    toggleSidebar() {
      this.sidebarVisible = !this.sidebarVisible
    }
  },
};
</script>